import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Avatar,
  Checkbox,
  List,
  ListItem,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { Edit, Delete } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import UpgradePlanDialog from "src/@core/components/UpgradePlanDialog";
import { setShowSubscriptionPlanDialog } from "src/redux/slices/AuthSlice";

// Add edit mutation
const useDeleteMenuItem = () => {
  return useMutation({
    mutationFn: async (menuItemId) => {
      const res = await axiosInstance.delete(
        `${ApiEndPoints.MENU_ITEM.add}/${menuItemId}`
      );
      return res?.data;
    },
  });
};

export default function SelectItems({
  handleClose,
  selectedItems,
  setSelectedItems,
  onCreateItem,
  onEditItem,
  onAdd,
  isCatalog = false, // Add isCatalog prop
}) {
  const [search, setSearch] = useState("");
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeDialogConfig, setUpgradeDialogConfig] = useState({
    featureType: "items",
    currentQuantity: 0,
    maxQuantity: 0,
  });
  
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  
  const user = useSelector((state) => state.auth.user);
  const selectedBusiness =
    user?.business?.filter(
      (business) => business._id === selectedBusinessId
    )[0] || null;

  const { data: menuItems } = useQuery(
    ["menuItems", selectedBusinessId, isCatalog], // Add isCatalog to query key
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU_ITEM.get}/${selectedBusinessId}`,
        { 
          signal,
          params: {
            isCatalog: isCatalog, // Add isCatalog parameter
          }
        }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 0, // Ensures fresh data after invalidation
      cacheTime: 30 * 60 * 1000,
      enabled: !!selectedBusinessId && isCatalog !== undefined && isCatalog !== null,
    }
  );

  const { mutate: deleteMenuItem, isLoading: isDeleting } = useDeleteMenuItem();

  const menuitems = useMemo(
    () =>
      Array.isArray(menuItems?.items)
        ? menuItems?.items.map((cat) => ({
            id: cat._id,
            name: cat.name,
            image: cat.imageVideo?.[0] || "",
            menuItemIds: cat?.menuItemIds || [],
          }))
        : [],
    [menuItems]
  );

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return menuitems;
    return menuitems.filter((cat) =>
      cat.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, menuitems]);

  // Check if object with id exists in selectedItems
  const isSelected = (id) => selectedItems.some((cat) => (cat._id || cat.id) === id);

  // Toggle: add/remove the whole category object
  const handleToggle = (catObj) => {
    setSelectedItems((prev) => {
      if (prev.some((c) => (c._id || c.id) === catObj.id)) {
        return prev.filter((c) => (c._id || c.id) !== catObj.id);
      } else {
        return [...prev, catObj];
      }
    });
  };

  const handleAddCategories = () => {
    onAdd();
    // handleClose();
    // If needed, you can call onAdd(selectedCategories) here
  };

  return (
    <Box
      sx={{
        maxWidth: {
          xs: "100vw",
          sm: "95vw",
          md: 500,
          lg: 600,
          xl: 700,
        },
        mx: "auto",
        bgcolor: "#fff",
        borderRadius: {
          xs: "24px",
          sm: 3,
          md: 4,
        },
        overflow: "hidden",
        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        boxSizing: "border-box",
        "@media (max-width: 600px)": {
          minWidth: "100vw",
          maxWidth: "100vw",
          borderRadius: "24px",
          boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          py: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 },
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: 18, sm: 20, md: 22, lg: 24 },
            color: "#000",
            letterSpacing: 0.5,
            textTransform: "lowercase",
            pr: { xs: 5, sm: 6 },
          }}
        >
          select Items
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 13,
            color: "#c09adf",
            background: "transparent",
            "&:hover": { color: "#8000c2", background: "#f3e7ff" },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TextField
          placeholder="search menu items..."
          fullWidth
          size="small"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mb: { xs: 1.5, sm: 2 },
            flexShrink: 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#f8f9fa",
              fontSize: { xs: 14, sm: 16 },
              "&:hover": {
                backgroundColor: "#f1f3f4",
              },
              "&.Mui-focused": {
                backgroundColor: "#fff",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            mb: { xs: 1.5, sm: 2 },
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            color: "#6c00e6",
            p: { xs: 0.5, sm: 1 },
            borderRadius: 2,
            transition: "all 0.2s",
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "#f3e8ff",
              transform: "translateX(2px)",
            },
            fontSize: { xs: 13, sm: 14 },
            fontWeight: 600,
          }}
          onClick={() => {
            const features = selectedBusiness?.features || [];
            const itemsfeature = features.find(
              (feature) => feature.featureKey === "items_per_menu"
            );
            if (itemsfeature.isFree) {
              onCreateItem();
            } else if (itemsfeature.quantity > menuitems?.length) {
              onCreateItem();
            } else {
              // Show upgrade dialog instead of redirecting
              setUpgradeDialogConfig({
                featureType: "items",
                currentQuantity: menuitems?.length || 0,
                maxQuantity: itemsfeature.quantity,
              });
              setShowUpgradeDialog(true);
            }
          }}
        >
          <AddCircleOutlineIcon
            fontSize="small"
            sx={{ mr: 1, color: "#6c00e6" }}
          />
          create new item
        </Box>

        <Box
          sx={{
            mb: { xs: 2, sm: 3 },
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 600,
              color: "#6b7280",
              mb: { xs: 1, sm: 1.5 },
              textTransform: "lowercase",
              letterSpacing: 0.5,
              flexShrink: 0,
            }}
          >
            available items ({filteredCategories.length})
          </Typography>
          <List
            sx={{
              flexGrow: 1,
              overflow: "auto",
              bgcolor: "#fafbfc",
              borderRadius: 2,
              p: { xs: 1, sm: 1.5 },
              minHeight: 0,
              maxHeight: { xs: "38vh", sm: "40vh", md: "45vh", lg: "52vh" },
              boxSizing: "border-box",
            }}
          >
            {filteredCategories.map((cat, i) => (
              <ListItem
                key={cat.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  mb: { xs: 2, sm: 2 },
                  width: "100%",
                  overflow: "hidden",
                  gap: 2,
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#222",
                      fontWeight: 500,
                      fontSize: 15,
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Checkbox
                    checked={isSelected(cat.id)}
                    onChange={(event) => {
                      event.stopPropagation();
                      handleToggle(cat);
                    }}
                    size="medium"
                    sx={{
                      color: "#6c00e6",
                      "&.Mui-checked": { color: "#6c00e6" },
                    }}
                  />

                  <Box sx={{ position: "relative", mr: 1 }}>
                    <Avatar
                      variant="rounded"
                      src={cat.image || undefined}
                      alt={cat.name}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: "#f3f4f6",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      fontSize: 16,
                      minWidth: 120,
                      textTransform: "capitalize",
                    }}
                  >
                    {cat.name}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Add your edit handler here
                      // console.log("Edit category:", cat.id);
                      const originalItem = menuItems.items.find(
                        (item) => item._id === cat.id
                      );
                      console.log("Original item:", originalItem);
                      onEditItem(originalItem);
                      // setSelectedItems([originalItem]);
                      // handleClose();
                    }}
                    sx={{
                      color: "#6c00e6",
                      "&:hover": {
                        bgcolor: "#f3e7ff",
                      },
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={isDeleting}
                    onClick={async (e) => {
                      e.stopPropagation();
                      // Add your delete handler here
                      console.log("Delete category:", cat.id);
                      await deleteMenuItem(cat.id);
                      queryClient.invalidateQueries([
                        "menuItems",
                        selectedBusinessId,
                        isCatalog, // Add isCatalog to invalidate correct query
                      ]);
                    }}
                    sx={{
                      color: "#dc2626",
                      "&:hover": {
                        bgcolor: "#fef2f2",
                      },
                    }}
                  >
                    {isDeleting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Delete fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </ListItem>
            ))}

            {!filteredCategories.length && (
              <Box
                sx={{
                  textAlign: "center",
                  py: { xs: 3, sm: 4 },
                  color: "#9ca3af",
                }}
              >
                <Typography sx={{ fontSize: { xs: 13, sm: 14 }, mb: 1 }}>
                  no item found
                </Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 12 } }}>
                  try adjusting your search or create a new category
                </Typography>
              </Box>
            )}
          </List>
        </Box>

        <Button
          variant="contained"
          fullWidth
          disabled={selectedItems.length === 0}
          sx={{
            bgcolor: "#6c00e6",
            color: "#fff",
            borderRadius: 2,
            fontWeight: 700,
            fontSize: { xs: 14, sm: 16 },
            textTransform: "lowercase",
            boxShadow: "0 4px 12px rgba(108, 0, 230, 0.3)",
            flexShrink: 0,
            "&:hover": {
              bgcolor: "#5a00c2",
              boxShadow: "0 6px 16px rgba(108, 0, 230, 0.4)",
            },
            "&:disabled": {
              bgcolor: "#e5e7eb",
              color: "#9ca3af",
              boxShadow: "none",
            },
            height: { xs: 44, sm: 48 },
            letterSpacing: 0.5,
          }}
          onClick={handleAddCategories}
        >
          add {selectedItems.length > 0 ? `${selectedItems.length} ` : ""}
          item
          {selectedItems.length === 1 ? "" : "s"}
        </Button>
      </Box>

      {/* Upgrade Plan Dialog */}
      <UpgradePlanDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        selectedBusiness={selectedBusiness}
        featureType={upgradeDialogConfig.featureType}
        currentQuantity={upgradeDialogConfig.currentQuantity}
        maxQuantity={upgradeDialogConfig.maxQuantity}
        onUpgrade={() => {
          dispatch(
            setShowSubscriptionPlanDialog({
              show: true,
              businessId: selectedBusinessId,
              currentPlan: selectedBusiness?.planName,
            })
          );
        }}
      />
    </Box>
  );
}
